import { useEffect, useState, useRef, useMemo } from "react";
import { Link } from "react-router-dom";
import { Grid, GridColumn } from "@progress/kendo-react-grid";
import { ExcelExport } from "@progress/kendo-react-excel-export";
import { process } from "@progress/kendo-data-query";
import { PrivateAxios } from "../../environment/AxiosInstance";
import { CurrencyList } from "../../utils/StaticData";
import { SuccessMessage, ErrorMessage } from "../../environment/ToastMessage";
import { UserAuth } from "../auth/Auth";
import TargetPIModal from "./TargetPIModal";
import TargetPIGroupModal from "./TargetPIGroupModal";
import moment from "moment";
import "@progress/kendo-theme-default/dist/all.css";
import "./TargetPI.css";

const PI_VIEW = ["Target PI All", "View Target PI", "Add Target PI", "Edit Target PI", "Delete Target PI"];

export default function TargetPI() {
  const { MatchPermission, AdminExist, userDetails } = UserAuth();

  const isAdminView =
    AdminExist || userDetails?.position === "Owner" || MatchPermission(["Target PI All"]);
  const canAdd = AdminExist || userDetails?.position === "Owner" || MatchPermission(["Target PI All", "Add Target PI"]);
  const canDelete = AdminExist || userDetails?.position === "Owner" || MatchPermission(["Target PI All", "Delete Target PI"]);
  const canEdit = MatchPermission(["Target PI All", "Add Target PI", "Edit Target PI", "Delete Target PI"]);
  const currentUserId = userDetails?.id;
  const ownsRow = (row) => String(row.employee_id) === String(currentUserId);
  const canEditRow = (row) => canEdit && (isAdminView || ownsRow(row));
  const canDeleteRow = (row) => canDelete && (isAdminView || ownsRow(row));

  const [view, setView] = useState("targets"); // "targets" | "log"
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({});
  const [logRows, setLogRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [logLoading, setLogLoading] = useState(false);
  const [logLoaded, setLogLoaded] = useState(false);
  const [employeeFilter, setEmployeeFilter] = useState("");
  const [searchText, setSearchText] = useState("");
  const [selected, setSelected] = useState(null);       // single member -> detail
  const [selectedGroup, setSelectedGroup] = useState(null); // multi-user -> breakdown
  const [dataState, setDataState] = useState({ skip: 0, take: 10, sort: [] });
  const [logState, setLogState] = useState({ skip: 0, take: 10, sort: [{ field: "date", dir: "desc" }] });
  const excelExportRef = useRef();
  const logExportRef = useRef();

  const canView = MatchPermission(PI_VIEW);
  const scope = isAdminView ? "all" : "mine";

  useEffect(() => {
    if (canView) fetchOverview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canView, isAdminView]);

  useEffect(() => {
    if (canView && view === "log" && !logLoaded) fetchLog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  const employeeOptions = useMemo(() => {
    const map = new Map();
    rows.forEach((r) => { if (!map.has(r.employee_id)) map.set(r.employee_id, r.employee_name); });
    logRows.forEach((r) => { if (!map.has(r.employee_id)) map.set(r.employee_id, r.employee_name); });
    return Array.from(map, ([id, name]) => ({ id, name }));
  }, [rows, logRows]);

  const matchSearch = (r) => {
    const empOk = !employeeFilter || String(r.employee_id) === String(employeeFilter);
    const q = searchText.trim().toLowerCase();
    const searchOk =
      !q ||
      (r.target_name || "").toLowerCase().includes(q) ||
      (r.employee_name || "").toLowerCase().includes(q) ||
      (r.customer_name || "").toLowerCase().includes(q);
    return empOk && searchOk;
  };

  // Member-level filtered rows, then grouped into one display row per target group.
  const filteredMembers = useMemo(() => rows.filter(matchSearch), [rows, employeeFilter, searchText]);

  const groupedRows = useMemo(() => {
    const map = new Map();
    filteredMembers.forEach((m) => {
      const key = m.group_id || `solo-${m.pi_target_id}`;
      if (!map.has(key)) {
        map.set(key, {
          key,
          target_name: m.target_name,
          from_date: m.from_date,
          to_date: m.to_date,
          currency: m.currency,
          members: [],
        });
      }
      map.get(key).members.push(m);
    });
    return Array.from(map.values()).map((g) => {
      const sum = (f) => g.members.reduce((s, m) => s + (Number(m[f]) || 0), 0);
      const assigned = sum("assigned_amount");
      const sales = sum("achievement_sales");
      const single = g.members.length === 1;
      return {
        ...g,
        is_group: g.members.length > 1,
        pi_target_id: g.members[0].pi_target_id,
        employee_id: single ? g.members[0].employee_id : null,
        employee_name: single ? g.members[0].employee_name : `${g.members.length} employees`,
        period: single ? g.members[0].period : null,
        assigned_amount: assigned,
        order_booking: sum("order_booking"),
        achievement_sales: sales,
        open_order: sum("open_order"),
        achievement_pct: assigned > 0 ? Number(((sales / assigned) * 100).toFixed(2)) : 0,
      };
    });
  }, [filteredMembers]);

  const filteredLog = useMemo(() => logRows.filter(matchSearch), [logRows, employeeFilter, searchText]);

  if (!canView) {
    return (
      <div className="p-4 text-center">
        <h4>Access Denied</h4>
        <p>You don't have permission to view the Target PI page.</p>
      </div>
    );
  }

  async function fetchOverview() {
    setLoading(true);
    try {
      const res = await PrivateAxios.get("/sales-pi/overview", { params: { scope } });
      setRows(Array.isArray(res.data?.data) ? res.data.data : []);
      setSummary(res.data?.summary || {});
    } catch (err) {
      console.error("Fetch Target PI overview error:", err);
      setRows([]); setSummary({});
    } finally { setLoading(false); }
  }

  async function fetchLog() {
    setLogLoading(true);
    try {
      const res = await PrivateAxios.get("/sales-pi/entries-log", { params: { scope } });
      setLogRows(Array.isArray(res.data?.data) ? res.data.data : []);
      setLogLoaded(true);
    } catch (err) {
      console.error("Fetch Target PI log error:", err);
      setLogRows([]);
    } finally { setLogLoading(false); }
  }

  async function deleteTarget(row) {
    if (!window.confirm(`Delete PI target "${row.target_name}" for ${row.employee_name}?`)) return;
    try {
      await PrivateAxios.delete(`/sales-pi/targets/${row.pi_target_id}`);
      SuccessMessage("PI target deleted");
      setSelectedGroup(null);
      fetchOverview();
      setLogLoaded(false);
    } catch (err) {
      ErrorMessage(err.response?.data?.message || "Failed to delete PI target");
    }
  }

  const ccySymbol = (code) => CurrencyList.find((c) => c.code === code)?.symbol || "₹";
  const fmtMoney = (val, code = "INR") =>
    `${ccySymbol(code)}${new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(Number(val) || 0)}`;

  // ---- cells ----
  const DateCell = (props) => {
    const v = props.dataItem[props.field];
    return <td>{v ? moment(v).format("DD-MM-YYYY") : "-"}</td>;
  };
  const MoneyCell = (field) => (props) => (
    <td className="text-end">{fmtMoney(props.dataItem[field], props.dataItem.currency)}</td>
  );
  const EmployeeCell = (props) => {
    const r = props.dataItem;
    return (
      <td>
        {r.is_group ? (
          <span className="pi-grouptag"><i className="fas fa-layer-group me-1"></i>{r.employee_name}</span>
        ) : (
          r.employee_name
        )}
      </td>
    );
  };
  const PctCell = (props) => {
    const pct = Number(props.dataItem.achievement_pct) || 0;
    const cls = pct >= 100 ? "bg-success" : pct >= 75 ? "bg-warning text-dark" : "bg-danger";
    return <td className="text-center"><span className={`pi-badge ${cls}`}>{pct}%</span></td>;
  };
  const openRow = (r) => (r.is_group ? setSelectedGroup(r) : setSelected(r));
  const ActionCell = (props) => {
    const r = props.dataItem;
    return (
      <td className="text-center">
        <div className="pi-actions">
          <button className="btn btn-sm btn-outline-primary pi-act-btn" title={r.is_group ? "View user-wise breakdown" : "Open PI detail"} onClick={() => openRow(r)}>
            <i className={`fas ${r.is_group ? "fa-users" : "fa-eye"}`}></i>
          </button>
          {!r.is_group && canDeleteRow(r) && (
            <button className="btn btn-sm btn-outline-danger pi-act-btn" title="Delete PI target" onClick={() => deleteTarget(r)}>
              <i className="fas fa-trash"></i>
            </button>
          )}
        </div>
      </td>
    );
  };
  const TypeCell = (props) => {
    const t = props.dataItem.type;
    const cls = t === "Order Booking" ? "pi-tag-booking" : "pi-tag-sales";
    return <td><span className={`pi-tag ${cls}`}>{t}</span></td>;
  };
  const RefCell = (props) => {
    const { customer_name, po_no } = props.dataItem;
    if (!customer_name && !po_no) return <td className="text-muted">-</td>;
    return <td>{customer_name || ""}{po_no ? <span className="text-muted"> · {po_no}</span> : ""}</td>;
  };

  const processed = process(groupedRows, dataState);
  const processedLog = process(filteredLog, logState);

  const stats = [
    { key: "target", label: "Total Target", icon: "fa-bullseye", value: summary.total_target },
    { key: "booking", label: "Order Booking", icon: "fa-cart-shopping", value: summary.total_order_booking },
    { key: "ach", label: "Achievement (Sales)", icon: "fa-arrow-trend-up", value: summary.total_achievement },
    { key: "open", label: "Open Order", icon: "fa-hourglass-half", value: summary.total_open_order },
  ];
  const iconMap = { "fa-cart-shopping": "fa-shopping-cart", "fa-arrow-trend-up": "fa-chart-line" };

  return (
    <div className="container-fluid target-pi-page">
      <div className="pi-stats">
        {stats.map((s) => (
          <div key={s.key} className={`pi-stat pi-stat--${s.key}`}>
            <div className="pi-stat__icon"><i className={`fas ${iconMap[s.icon] || s.icon}`}></i></div>
            <div className="pi-stat__body">
              <span className="pi-stat__label">{s.label}</span>
              <span className="pi-stat__value">{fmtMoney(s.value)}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="pi-card">
        <div className="pi-card__header">
          <div className="d-flex align-items-center gap-3 flex-wrap">
            <h5 className="pi-card__title mb-0">
              <i className="fas fa-chart-pie me-2"></i>
              Target PI{!isAdminView && <span className="text-muted fw-normal ms-2" style={{ fontSize: "0.85rem" }}>My Targets</span>}
            </h5>
            <div className="pi-viewtoggle">
              <button className={`pi-viewtoggle__btn ${view === "targets" ? "active" : ""}`} onClick={() => setView("targets")}>
                <i className="fas fa-bullseye me-1"></i> Targets
              </button>
              <button className={`pi-viewtoggle__btn ${view === "log" ? "active" : ""}`} onClick={() => setView("log")}>
                <i className="fas fa-list me-1"></i> Activity Log
              </button>
            </div>
          </div>
          <div className="pi-toolbar">
            {isAdminView && (
              <select className="form-select form-select-sm" style={{ width: "auto" }} value={employeeFilter} onChange={(e) => setEmployeeFilter(e.target.value)}>
                <option value="">All Employees</option>
                {employeeOptions.map((e) => (<option key={e.id} value={e.id}>{e.name}</option>))}
              </select>
            )}
            <input type="text" className="form-control form-control-sm" style={{ width: "auto" }} placeholder="Filter target / employee / customer" value={searchText} onChange={(e) => setSearchText(e.target.value)} />
            {canAdd && view === "targets" && (
              <Link className="btn btn-primary btn-sm" to="/sales/add-pi-target"><i className="fas fa-plus me-1"></i> Add PI Target</Link>
            )}
            <button className="btn btn-outline-success btn-sm" onClick={() => (view === "targets" ? excelExportRef.current?.save() : logExportRef.current?.save())}>
              <i className="fas fa-file-excel me-1"></i> Download
            </button>
          </div>
        </div>

        <div className="p-0">
          {view === "targets" ? (
            <ExcelExport data={groupedRows} ref={excelExportRef} fileName={`Target_PI_${moment().format("YYYY-MM-DD")}.xlsx`}>
              <Grid data={processed} {...dataState} onDataStateChange={(e) => setDataState(e.dataState)} sortable={true}
                pageable={{ pageSizes: [5, 10, 20, 50], buttonCount: 5 }} resizable={true} loading={loading} style={{ width: "100%" }}>
                <GridColumn field="employee_name" title="Employee Name" width="180px" cell={EmployeeCell} />
                <GridColumn field="target_name" title="Target Name" width="160px" />
                <GridColumn field="from_date" title="From" width="110px" cell={DateCell} />
                <GridColumn field="to_date" title="To" width="110px" cell={DateCell} />
                <GridColumn field="assigned_amount" title="Assigned (Target)" width="140px" cell={MoneyCell("assigned_amount")} />
                <GridColumn field="order_booking" title="Order Booking" width="140px" cell={MoneyCell("order_booking")} />
                <GridColumn field="achievement_sales" title="Achievement (Sales)" width="150px" cell={MoneyCell("achievement_sales")} />
                <GridColumn field="open_order" title="Open Order" width="130px" cell={MoneyCell("open_order")} />
                <GridColumn field="achievement_pct" title="Achievement %" width="120px" cell={PctCell} sortable={true} />
                <GridColumn title="Action" width="120px" sortable={false} cell={ActionCell} />
              </Grid>
            </ExcelExport>
          ) : (
            <ExcelExport data={filteredLog} ref={logExportRef} fileName={`Target_PI_Activity_${moment().format("YYYY-MM-DD")}.xlsx`}>
              <Grid data={processedLog} {...logState} onDataStateChange={(e) => setLogState(e.dataState)} sortable={true}
                pageable={{ pageSizes: [10, 20, 50, 100], buttonCount: 5 }} resizable={true} loading={logLoading} style={{ width: "100%" }}>
                <GridColumn field="employee_name" title="Employee Name" width="170px" />
                <GridColumn field="target_name" title="Target Name" width="160px" />
                <GridColumn field="type" title="Type" width="160px" cell={TypeCell} />
                <GridColumn field="date" title="Date" width="120px" cell={DateCell} />
                <GridColumn field="customer_name" title="Customer / PO" width="200px" cell={RefCell} sortable={false} />
                <GridColumn field="amount" title="Amount" width="140px" cell={MoneyCell("amount")} />
              </Grid>
            </ExcelExport>
          )}
        </div>
      </div>

      {/* Multi-user breakdown */}
      {selectedGroup && (
        <TargetPIGroupModal
          group={selectedGroup}
          canEditRow={canEditRow}
          onOpenMember={(m) => { setSelectedGroup(null); setSelected(m); }}
          onDeleteMember={(m) => deleteTarget(m)}
          onClose={() => setSelectedGroup(null)}
        />
      )}

      {/* Single-user detail */}
      {selected && (
        <TargetPIModal row={selected} canEdit={canEditRow(selected)}
          onClose={() => { setSelected(null); fetchOverview(); setLogLoaded(false); }} />
      )}
    </div>
  );
}
